'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';

import { Title } from '@/styles/global.elements';
import { SearchBar } from '@/elements/search-bar/search-bar.component';
import { EducationCard } from '@/elements/education-card/education-card.component';
import { Button } from '@/elements/button/button.component';
import { CardsGrid } from '@/elements/cards-grid/cards-grid.component';
import { Container, UpcomingEducations } from './educations-widget.elements';

import { getEducationsByTag } from '@/actions/get-educations-by-tag.action';
import { filterEducations } from '@/helpers/education-filters.helpers';

const EducationsWidget = ({ featured, tags, upcoming }) => {
  const [educations, setEducations] = useState(featured);
  const [activeTag, setActiveTag] = useState('featured');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState([]);
  const [mounted, setMounted] = useState(false);
  const isMountedRef = useRef(true);

  const activeFilters =
    search.trim().length > 0
      ? [...filters, { id: 'live-search', value: search.trim(), type: 'search' }]
      : filters;

  // Track component mount state to prevent state updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /*
   * Updated logic:
   * handleTagClick only sets the active tag.
   * useEffect handles data fetching whenever activeTag or filters change.
   * This prevents the bug where useEffect was resetting activeTag to 'featured'
   * because it detected a change in handleFilterEducations (due to activeTag change)
   * and fell into the "no filters" else block.
   */
  const handleTagClick = useCallback((tag) => {
    setActiveTag(tag);
  }, []);

  const handleFilterEducations = useCallback(
    async ({ filters: currentFilters }) => {
      // Handle 'discount' tag specially
      if (activeTag === 'discount') {
        const discountEducations = await getEducationsByTag({
          filters: currentFilters,
          tag: 'discount',
        });
        if (isMountedRef.current) {
          setEducations(discountEducations);
        }
        return;
      }

      // Get base educations for current tag
      const baseEducations = await getEducationsByTag({ filters: [], tag: activeTag });

      if (isMountedRef.current) {
        // Apply filters client-side
        const newEducations = filterEducations(baseEducations, currentFilters);
        setEducations(newEducations);
      }
    },
    [activeTag]
  );

  useEffect(() => {
    // If we are on featured tab and have no filters, utilize the initial 'featured' data
    // to avoid unnecessary re-fetching.
    if (activeTag === 'featured' && activeFilters.length === 0) {
      setEducations(featured);
      return;
    }

    // Otherwise, fetch/filter based on current state
    handleFilterEducations({ filters: activeFilters });
  }, [activeFilters, activeTag, featured, handleFilterEducations]);

  useEffect(() => {
    // Ensure Mantine Carousel renders only on the client to avoid hydration mismatches
    setMounted(true);
  }, []);

  return (
    <Container>
      <Title>Pronađi edukaciju za sebe</Title>
      <SearchBar
        setEducations={setEducations}
        search={search}
        setSearch={setSearch}
        filters={filters}
        setFilters={setFilters}
        tags={tags}
        onTagClick={handleTagClick}
        activeTag={activeTag}
        target="educations"
      />
      <CardsGrid
        data={educations}
        Card={EducationCard}
        withSearch
        filters={filters}
        type="education"
      />
      <Link href="/edukacije" style={{ margin: '0 auto' }}>
        <Button naked icon="arrow">
          Pogledaj više
        </Button>
      </Link>
      {mounted && upcoming.length > 0 ? (
        <>
          <Title $medium>Edukacije s nadolazećim susretom</Title>
          <UpcomingEducations
            withIndicators={false}
            emblaOptions={{ align: 'start', dragFree: true, slidesToScroll: 1 }}
          >
            {upcoming.map((education) => (
              <div className="card" key={education.id}>
                <EducationCard data={education} />
              </div>
            ))}
          </UpcomingEducations>
          <Link href="/kalendar" style={{ margin: '0 auto' }}>
            <Button naked icon="arrow">
              Pogledaj cijeli kalendar
            </Button>
          </Link>
        </>
      ) : null}
    </Container>
  );
};

export { EducationsWidget };
